import { Base, withMountOnMediaQuery } from '@studiometa/js-toolkit';
import { addClass, removeClass } from '@studiometa/js-toolkit/utils';

/**
 * Le méga menu est géré en CSS pur au survol sur desktop : ce composant n'est
 * monté qu'en dessous du breakpoint `m` (1024px), où le panneau s'ouvre au clic
 * par dessus le menu mobile.
 */
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

/**
 * @typedef {{
 *   $refs: {
 *     trigger: HTMLAnchorElement;
 *     label: HTMLElement;
 *     panel: HTMLElement | undefined;
 *     back: HTMLButtonElement | undefined;
 *     backLabel: HTMLElement | undefined;
 *   };
 * }} MegaMenuProps
 */

/**
 * MegaMenu component
 * @augments {Base<MegaMenuProps>}
 */
export default class MegaMenu extends withMountOnMediaQuery(Base, MOBILE_MEDIA_QUERY) {
  /**
   * Component Configuration
   */
  static config = {
    name: 'MegaMenu',
    refs: ['trigger', 'label', 'panel', 'back', 'backLabel'],
  };

  /**
   * Les items de menu sans bloc méga menu enfant n'ont pas de panneau.
   * @returns {boolean}
   */
  get hasPanel() {
    return Boolean(this.$refs.panel);
  }

  /**
   * Reprend le libellé de l'item pour le bouton retour du panneau.
   */
  mounted() {
    if (!this.hasPanel) {
      return;
    }

    this.$refs.backLabel.textContent = this.$refs.label.textContent.trim();
    this.$refs.trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * Referme le panneau au passage en desktop, où le survol prend le relais.
   */
  destroyed() {
    this.close();
  }

  /**
   * En mobile, le lien de l'item ouvre le panneau au lieu de naviguer.
   * @param {object} props Props
   * @param {PointerEvent} props.event The click event.
   */
  onTriggerClick({ event }) {
    if (!this.hasPanel) {
      return;
    }

    event.preventDefault();
    this.open();
  }

  /**
   * Bouton retour du panneau
   */
  onBackClick() {
    this.close();
  }

  /**
   * Ouvre le panneau
   */
  open() {
    if (!this.hasPanel) {
      return;
    }

    addClass(this.$refs.panel, 'is-open');
    this.$refs.trigger.setAttribute('aria-expanded', 'true');
    this.$refs.back.focus();
  }

  /**
   * Ferme le panneau
   */
  close() {
    if (!this.hasPanel) {
      return;
    }

    removeClass(this.$refs.panel, 'is-open');
    this.$refs.trigger.setAttribute('aria-expanded', 'false');
  }
}
